import BannerPlan from "components/BannerPlan";
import Button from "components/Button";
import CouponInputAuth from "components/CouponInputAuth";
import Loading from "components/Loading";
import Dashboard from "components/Template/Dashboard";
import { useAuth } from "context/auth";
import handleErrors from "helpers/handleErrors";
import toastMessage from "helpers/handleToastMessage";
import ArrowLeft from "icons/arrow-circle-left-green.svg";
import IconArrowRight from "icons/arrow-circle-right-white.svg";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { checkCoupon } from "services/coupon";
import { UpdateSubscription } from "services/manageFamilies";
import { listPlan } from "services/plan";
import { Plan } from "types/plan";
import { getPlansWithDiscounts } from "utils/getPlansWithDiscount";
import { schemaChangePlan } from "./validation";

type Props = {};

const UpdatePlan: React.FC<Props> = (): JSX.Element => {
  let history = useHistory();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>();
  const [idSelected, setIdSelected] = useState("");
  const [plans, setPlans] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [isValidCoupon, setIsValidCoupon] = useState<boolean>(false);
  const [couponData, setCouponData] = useState<any>();
  const [originalValueMonth, setOriginalValueMonth] = useState<number>(0);
  const [originalValueOneTime, setOriginalValueOneTime] = useState<number>(0);

  const { userCoupon } = useAuth();

  const getPlansList = useCallback(async () => {
    try {
      setLoading(true);

      let plansWithDiscount = [];
      const { data } = await listPlan();
      if (data.Plans.length > 0) {
        plansWithDiscount = data.Plans.filter(
          (plan: Plan) => plan.active === true && plan.type === "one_time"
        );
      }

      plansWithDiscount = await getPlansWithDiscounts({
        coupon: userCoupon,
        currentPlans: plansWithDiscount,
      });

      plansWithDiscount.forEach((plan: Plan) => {
        if (plan.type === "one_time") {
          setOriginalValueOneTime(plan.unit_amount);
        }
        if (plan.type === "recurring") {
          setOriginalValueMonth(plan.unit_amount);
        }
      });

      setPlans(plansWithDiscount);
    } catch (error: any) {
      console.log(error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setGroupId(history.location.pathname.split("/")[2]);
    getPlansList();
  }, [history, getPlansList]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (
        !schemaChangePlan.isValidSync({
          idSelected,
        })
      ) {
        handleErrors({
          schema: schemaChangePlan,
          stateData: {
            idSelected,
          },
          setErrorMessage: setErrors,
        });
        setLoading(false);
        return;
      }

      await UpdateSubscription({
        coupons: [userCoupon, couponCode],
        priceId: idSelected,
        groupId,
      });

      toastMessage({
        message: "Subscription update!",
        type: "success",
      });

      history.push(`/manage-families/${groupId}/details`);
    } catch (error: any) {
      toastMessage({
        message: "Error, Something's wrong when change subscription",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const validCoupon = async (sendCoupon: string) => {
    try {
      if (couponData?.id === sendCoupon) {
        return;
      }

      await getPlansList();
      setLoading(true);

      let plansWithDiscount = plans;
      const { data } = await checkCoupon(sendCoupon);

      if (sendCoupon) {
        plansWithDiscount = await getPlansWithDiscounts({
          coupon: sendCoupon,
          currentPlans: plansWithDiscount,
          originalValueMonth,
          originalValueOneTime,
        });
      }

      setPlans(plansWithDiscount);
      setCouponData(data);
      setIsValidCoupon(true);
    } catch (error) {
      await getPlansList();
      setCouponCode("");
      setCouponData("COUPON NOT VALID!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dashboard
      icon={ArrowLeft}
      breadcrumb={[
        { name: "Manage Families", active: false, path: `/manage-families` },
        {
          name: history.location.state.groupName,
          active: false,
          path: `/manage-families/${groupId}/details`,
        },
        { name: "Change Plan", active: true },
      ]}
      title={"Change your plan"}
      children={
        <div className="bg-bt-white p-4">
          <Loading loading={loading} />
          <div className="w-9/12 mx-auto mt-4">
            <div className="flex flex-row justify-center items-center mt-8">
              {plans.map((p: any) => {
                return (
                  <div className="flex flex-col mx-4" key={p.id}>
                    <BannerPlan
                      selected={idSelected === p.id}
                      onClick={() => setIdSelected(p.id)}
                      title={p.type}
                      value={parseFloat(
                        p.unit_amount
                          .toString()
                          .slice(0, -2)
                          .concat(`.${p.unit_amount.toString().slice(-2)}`)
                      ).toFixed(2)}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex flex-col justify-center items-center my-6">
              <div className="flex flex-col w-full">
                <CouponInputAuth
                  placeholder={"Coupon code"}
                  label="Coupon code"
                  value={couponCode}
                  onChange={(e: any) => setCouponCode(e.target.value)}
                  onBlur={() => validCoupon(couponCode)}
                />
              </div>
              <div className="flex flex-col my-auto">
                <span
                  className="text-base font-sora mt-5 ml-4"
                  style={{
                    fontStyle: "normal",
                    fontWeight: 400,
                    color: isValidCoupon ? "#408E5A" : "#FD5B28",
                  }}
                >
                  {couponData && (isValidCoupon ? couponData.name : couponData)}
                </span>
              </div>
            </div>
            {errors?.idSelected && (
              <div className="flex flex-row justify-center items-center mt-8">
                <span className="font-nunito mt-1 text-base text-bt-red">
                  {errors?.idSelected}
                </span>
              </div>
            )}
            <div className="flex flex-row justify-end mt-5">
              <Button
                label={"Confirm"}
                className="h-14 w-32 bg-bt-green text-base text-white rounded-full font-sora font-medium2"
                iconRight={IconArrowRight}
                onClick={handleSubmit}
              />
            </div>
          </div>
          <ToastContainer />
        </div>
      }
    />
  );
};

export default UpdatePlan;
